import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';

// Components
import HouseholdDetailsInfo from './household-details/HouseholdDetailsInfo';
import HouseholdDetailsTabs from './household-details/HouseholdDetailsTabs';
import NavigationSidebar from '@/views/components/navigation/NavigationSidebar';
import { Header } from '@/views/components/layout';

// Types
import Dictionary from '@/types/dictionary';
import GenericContact from '@/types/generic-contact';
import User from '@/types/user';
import { GenericHouseHold, Household } from '@/types/households';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { getContactName } from '@/utils/contacts';
import { NAV_PATHS } from '@/utils/constants';
import { showError } from '@/utils/errors';
import useRedirect from '@/hooks/useRedirect';

// Redux
import { getHouseholdComparisons, getHouseholdPortfolios, getHouseholdById, setMyContextHouseholdId } from '@/redux/modules/household';
import { startLoaderAction } from '@/redux/modules/ui/loaders';

type StateProps = {
  currentHousehold?: GenericHouseHold;
  householdContacts?: GenericContact[];
  user: User;
};

type DispatchProps = {
  getHouseholdComparisons: () => void;
  getHouseholdById: (id: string) => Promise<Household>;
  getHouseholdData: (households: GenericHouseHold[]) => void;
  getHouseholdPortfolios: () => void;
  startLoaderAction: (name: string, params?: Dictionary<unknown>) => void;
  setMyContextHouseholdId: (id: string) => void;
};

type Props = StateProps & DispatchProps;

const HouseholdLanding = (props: Props) => {
  const { currentHousehold, getHouseholdById, getHouseholdComparisons, getHouseholdPortfolios, setMyContextHouseholdId } = props;

  const { redirect } = useRedirect();

  // Get the householdId from URL params
  const { householdId } = useParams<{ householdId: string }>();

  useEffect(() => {
    getHouseholdInfo();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (currentHousehold?.id) {
      setMyContextHouseholdId(currentHousehold.id);
      getHouseholdComparisons();
      getHouseholdPortfolios();
    }
    // eslint-disable-next-line
  }, [currentHousehold, getHouseholdComparisons, getHouseholdPortfolios]);

  const getHouseholdInfo = async () => {
    if (householdId) {
      try {
        await getHouseholdById(householdId);
      } catch (err: any) {
        if (err.status === 404) {
          redirect(NAV_PATHS.COMPARISONS);
        } else {
          showError(err);
        }
      }
    }
  };

  const renderPageContent = () => (
    <>
      <HouseholdDetailsInfo currentHousehold={currentHousehold} />
      <HouseholdDetailsTabs />
    </>
  );

  const getNavSidebarItems = () => {
    if (!currentHousehold) {
      return [];
    }

    const householdNavItem = {
      id: currentHousehold.id,
      value: `/households/${currentHousehold.id}`,
      label: currentHousehold.name,
      icon: 'ds-groups',
    };

    const contactNavItems =
      currentHousehold.contacts.map(contact => ({
        id: contact._id!,
        value: `/households/${currentHousehold.id}/contacts/${contact._id}/portfolios`,
        label: getContactName(contact) || '',
      })) || [];

    return [householdNavItem, ...contactNavItems];
  };

  return (
    <div className="household-landing">
      <Header />

      <div className="household-landing-content">
        <NavigationSidebar items={getNavSidebarItems()} defaultExpanded defaultActiveId={currentHousehold?.id} />

        <main id="content" className="household-details-container">
          <div className="household-details-body">
            <div className="household-details-content">{renderPageContent()}</div>
          </div>
        </main>
      </div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  user: state.auth.user as User,
  currentHousehold: state.households.currentHousehold,
});

export default connect(mapStateToProps, {
  getHouseholdById,
  getHouseholdComparisons,
  getHouseholdPortfolios,
  setMyContextHouseholdId,
  startLoaderAction,
})(HouseholdLanding);
